import React from "react";
import { BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Funnel, SkeletonRows } from "@/components/dash/ui";

const RANGES = [["week", "This Week"], ["month", "This Month"], ["quarter", "This Quarter"], ["year", "This Year"]];

export default function Analytics() {
  const { user } = useAuth();
  const [range, setRange] = React.useState("month");
  const [growth, setGrowth] = React.useState([]);
  const [funnel, setFunnel] = React.useState([]);
  const [geo, setGeo] = React.useState([]);
  const [dist, setDist] = React.useState([]);

  React.useEffect(() => {
    api.get("/analytics/growth").then((r) => setGrowth(r.data.data)).catch(() => {});
    api.get("/analytics/funnel").then((r) => setFunnel(r.data.stages)).catch(() => {});
    api.get("/analytics/geo").then((r) => setGeo(r.data.data)).catch(() => {});
    api.get("/analytics/distribution").then((r) => setDist(r.data.data)).catch(() => {});
  }, []);

  const title = user.role === "staffing" ? "Recruitment Analytics" : user.role === "vendor" ? "Business Performance" : "Business Analytics";

  return (
    <div>
      <PageHeader title={title} description="Interactive insights across your workspace." breadcrumb="Analytics">
        <div className="flex rounded-lg border border-slate-200 bg-white p-0.5">
          {RANGES.map(([k, l]) => (
            <button key={k} onClick={() => setRange(k)} data-testid={`analytics-range-${k}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-md ${range === k ? "bg-vs-royal text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
      </PageHeader>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Activity Trend" subtitle="Requirements, submissions & quotations">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={growth} margin={{ left: -20 }}>
              <defs><linearGradient id="ag" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#1D4ED8" stopOpacity={0.25} /><stop offset="95%" stopColor="#1D4ED8" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
              <Area type="monotone" dataKey="submissions" stroke="#1D4ED8" fill="url(#ag)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Conversion Funnel" subtitle="Pipeline stage conversion">{funnel.length ? <Funnel stages={funnel} /> : <SkeletonRows rows={6} />}</Card>
        <Card title="Location Distribution" subtitle="Activity by location">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={geo} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="location" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={80} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
              <Bar dataKey="value" fill="#2563EB" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Ecosystem Distribution" subtitle="Partner composition">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart><Pie data={dist} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>{dist.map((e, i) => <Cell key={i} fill={e.color} />)}</Pie>
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} /><Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} /></PieChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
